import { Inject, Injectable } from '@angular/core';
import { RestService } from 'src/app/core/services/rest.service';
import {
    BillingMilestoneModel,
    BusinessActivityModel,
    PhaseModel,
    ProjectModel,
    ResearchCategory,
    SubcontractorInvoiceModel,
} from './project.models';
import { Pagination } from './project.state';
import { User } from 'src/app/core/models/user.model';

@Injectable({
    providedIn: 'root',
})
export class ProjectsService {
    constructor(@Inject(RestService) private restService: RestService) {}

    public async getActivities(): Promise<BusinessActivityModel[]> {
        const path = '/projects/activities';
        return this.restService.get(path, {});
    }

    public async getResearchCategories(): Promise<ResearchCategory[]> {
        const path = '/projects/researchCategories';
        return this.restService.get(path, {});
    }

    public async getLeadUsers(): Promise<User[]> {
        const path = '/projects/leadusers';
        return this.restService.get(path, {});
    }
    public async getProjects(
        pagination: Pagination,
    ): Promise<{ total: number; items: ProjectModel[] }> {
        const path = '/projects';
        const query: any = {};

        if (pagination.activityId && pagination.activityId.length > 0)
            query.activityId = pagination.activityId;
        if (pagination.leadUser && pagination.leadUser.length > 0)
            query.leadUser = pagination.leadUser;
        if (pagination.limit) query.limit = pagination.limit;
        if (pagination.offset) query.offset = pagination.offset;
        if (pagination.q) query.q = pagination.q;
        if (pagination.status && pagination.status.length > 0) query.status = pagination.status;
        if (pagination.sort?.key) query.sortKey = pagination.sort.key;
        if (pagination.sort?.direction) query.sortDirection = pagination.sort.direction;

        const { total, items } = await this.restService.get(path, query);
        return { total, items };
    }

    public async getProjectById(projectId: string): Promise<ProjectModel> {
        const path = `/projects/${projectId}`;
        return this.restService.get(path, {});
    }

    public async getProjectByCode(code: string): Promise<ProjectModel> {
        const path = `/projects/code/${code}`;
        return this.restService.get(path, {});
    }

    public async getProjectByPhaseId(phaseId: string): Promise<ProjectModel> {
        const path = `/projects/phase/${phaseId}`;
        return this.restService.get(path, {});
    }

    public async updateProject(project: Partial<ProjectModel>): Promise<ProjectModel> {
        const path = `/projects/${project.id}`;
        return await this.restService.patch(path, {}, project);
    }

    public async createProject(project: Partial<ProjectModel>): Promise<ProjectModel> {
        const path = `/projects/`;
        return await this.restService.post(path, {}, project);
    }

    public async createPhase(phase: Partial<PhaseModel>, projectId: string): Promise<PhaseModel> {
        const data = { ...phase, projectId };
        const path = `/projects/${projectId}/phases`;
        return await this.restService.post(path, {}, data);
    }

    public async updatePhase(phase: Partial<PhaseModel>, projectId: string): Promise<PhaseModel> {
        const data = { ...phase, projectId };
        const path = `/projects/${projectId}/phases/${phase.id}`;
        return await this.restService.patch(path, {}, data);
    }

    public async updateBillingMilestone(
        billingMilestone: Partial<BillingMilestoneModel>,
        projectId: string,
        updateSoldAmount: boolean,
    ): Promise<BillingMilestoneModel[]> {
        const data = billingMilestone;
        const path = `/projects/${projectId}/billingmilestone/${updateSoldAmount}`;
        return await this.restService.patch(path, {}, data);
    }

    public async addBillingMilestones(
        billingMilestones: Partial<BillingMilestoneModel>[],
        projectId: string,
        updateSoldAmount: boolean,
    ): Promise<BillingMilestoneModel[]> {
        const data = billingMilestones;
        const path = `/projects/${projectId}/billingmilestone/${updateSoldAmount}`;
        return await this.restService.post(path, {}, data);
    }

    public async deleteBillingMilestone(
        billingMilestoneId: string,
        projectId: string,
        updateSoldAmount: boolean,
    ): Promise<BillingMilestoneModel[]> {
        const path = `/projects/${projectId}/billingmilestone/${billingMilestoneId}/${updateSoldAmount}`;
        return await this.restService.delete(path, {});
    }

    public async updateSubcontractorInvoice(
        subcontractorInvoice: Partial<SubcontractorInvoiceModel>,
        projectId: string,
        updateSubcontractingAmount: boolean,
    ): Promise<SubcontractorInvoiceModel[]> {
        const data = subcontractorInvoice;
        const path = `/projects/${projectId}/subcontractorinvoice/${updateSubcontractingAmount}`;
        return await this.restService.patch(path, {}, data);
    }

    public async addSubcontractorInvoices(
        subcontractorinvoices: Partial<SubcontractorInvoiceModel>[],
        projectId: string,
        updateSubcontractingAmount: boolean,
    ): Promise<SubcontractorInvoiceModel[]> {
        const data = subcontractorinvoices;
        const path = `/projects/${projectId}/subcontractorinvoice/${updateSubcontractingAmount}`;
        return await this.restService.post(path, {}, data);
    }

    public async deleteSubcontractorInvoice(
        subcontractorInvoiceId: string,
        projectId: string,
        updateSubcontractingAmount: boolean,
    ): Promise<SubcontractorInvoiceModel[]> {
        const path = `/projects/${projectId}/subcontractorinvoice/${subcontractorInvoiceId}/${updateSubcontractingAmount}`;
        return await this.restService.delete(path, {});
    }
}
